import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, itemCount: 0 });
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [cartAnimation, setCartAnimation] = useState(false); // 購物車動畫狀態

  const fetchCart = async (sid) => {
    try {
      const response = await api.get('/cart', {
        headers: { 'x-session-id': sid },
        timeout: 5000 // 5 second timeout
      });
      setCart(response.data);
    } catch (error) {
      console.error('取得購物車失敗:', error);
      // Set empty cart on error to allow page to load
      setCart({ items: [], subtotal: 0, itemCount: 0 });
    }
  };

  // 初始化或取得 sessionId
  useEffect(() => {
    let sid = localStorage.getItem('cart_session_id');
    if (!sid) {
      sid = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('cart_session_id', sid);
    }
    setSessionId(sid);
    fetchCart(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addToCart = async (productId, quantity = 1, specInfo = null) => {
    setLoading(true);
    try {
      const response = await api.post('/cart', {
        product_id: productId,
        quantity,
        spec_info: specInfo
      }, {
        headers: { 'x-session-id': sessionId }
      });
      setCart(response.data);
      toast.success('已加入購物車！');
      // 觸發購物車動畫
      setCartAnimation(true);
      setTimeout(() => setCartAnimation(false), 800);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.error || '加入購物車失敗');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 以購物車列 id 為鍵（支援同商品多規格並存）；cartId 對應 cart.items[].id
  const updateQuantity = async (cartId, quantity) => {
    setLoading(true);
    try {
      const response = await api.put(`/cart/${cartId}`, { quantity }, {
        headers: { 'x-session-id': sessionId }
      });
      setCart(response.data);
    } catch (error) {
      toast.error(error.response?.data?.error || '更新數量失敗');
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (cartId) => {
    setLoading(true);
    try {
      const response = await api.delete(`/cart/${cartId}`, {
        headers: { 'x-session-id': sessionId }
      });
      setCart(response.data);
      toast.info('已從購物車移除');
    } catch (error) {
      toast.error('移除商品失敗');
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      const response = await api.delete('/cart', {
        headers: { 'x-session-id': sessionId }
      });
      setCart(response.data);
    } catch (error) {
      toast.error('清空購物車失敗');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      sessionId,
      cartAnimation,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      refreshCart: () => fetchCart(sessionId)
    }}>
      {children}
    </CartContext.Provider>
  );
};
